/** author: 胡枭 */

import router from './router'
import globalRoutes from '@/router/common'

import store from './store'
import Cookies from 'js-cookie'
import alifeLogger from '@/plugin/alife-logger'
import { getPlatformFormKeyApi } from '@/api/platform'
import { platformFunctionModuleDict } from '@/dictionary/system'
import { cloneDeep } from 'lodash'

/**
 * 判断当前路由是否有权限跳转，没有权限返回true
 * @param targetRoute 跳转的目标路由对象
 * @return boolean
 */
function hasNoRight(targetRoute) {
  if (
    targetRoute?.meta?.name &&
    store.state.router.noRightRouteIdList.includes(targetRoute?.meta?.name)
  ) {
    return true
  }

  return false
}

/**
 * 对于需要formKey的页面，先向后端获取formKey值，再进行跳转
 * @return Promise<String>
 */
function getFormKey(targetRoutePath) {
  return new Promise((resolve, reject) => {
    getPlatformFormKeyApi({ formName: targetRoutePath })
      .then(res => {
        resolve(res?.data?.key || '')
      })
      .catch(err => {
        reject(err)
      })
  })
}

/**
 * 获取员工的权限及401路由，并返回员工权限路由
 */
function getUserRightRouteListSync(companyInfo, userInfo) {
  return new Promise((resolve, reject) => {
    console.log('current env VUE_APP_NODE_ENV：', process.env.VUE_APP_NODE_ENV)
    // 遍历后端返回的路由数组，将属性parentId提升，以备后面转化为树做准备
    // 通过遍历所有路由的角色，得出整个平台所拥有的角色权限
    // 通过遍历所有路由的角色，得出路由中的灰度权限
    const greyPermissionList = [] // 路由中存在的灰度权限字段列表

    const routeListTemp = (companyInfo?.menus || [])
      .map(value => {
        value.parentId = value?.parentInfo?.parentId || 0
        value.routeInfo.meta = JSON.parse(value?.routeInfo?.meta || '{}')

        // TODO: 风险治理的限制
        if (
          process.env.VUE_APP_NODE_ENV === 'production' &&
          value.menuInfo.name === '风险治理' &&
          companyInfo.companyId !== 27481 &&
          companyInfo.companyId !== 4922 &&
          companyInfo.companyId !== 5064 && // 建材集团
          companyInfo.companyId !== 497 && // 江西省天然气管道有限公司
          companyInfo.companyId !== 40944 && // 江西省水利投资集团有限公司
          companyInfo.companyId !== 40986 && // 江西省水务集团有限公司
          companyInfo.companyId !== 28965 && // 江西省水投建设集团有限公司
          companyInfo.companyId !== 41303 && // 江西省水投能源发展有限公司
          companyInfo.companyId !== 41305 && // 江西省水投生态资源有限公司
          companyInfo.companyId !== 28244 && // 江西省水投工程咨询集团有限公司
          companyInfo.companyId !== 41056 && // 江西水投资本管理有限公司
          companyInfo.companyId !== 21388 && // 江西省水投江河信息技术有限公司
          companyInfo.companyId !== 40889 && // 江西省赣房投资集团有限公司
          companyInfo.companyId !== 40827 && // 江西省赣农投资发展集团有限公司
          companyInfo.companyId !== 41151 && // 江西润泉科工股份有限公司
          companyInfo.companyId !== 41467 && // 江西省赣勤发展集团有限公司
          companyInfo.companyId !== 40687 && // 南昌市监狱
          companyInfo.companyId !== 36091 && // 广西物流职业技术学院
          companyInfo.companyId !== 177 && // 生产测试单位
          companyInfo.companyId !== 1
        ) {
          return undefined
        }

        // 记录灰度权限
        if (value?.routeInfo?.meta?.grayscale) {
          greyPermissionList.push({
            code: value.routeInfo.meta.grayscale,
            companyIds: [companyInfo.companyId]
          })
        }

        // TODO：这里为特殊需求，在用户为会员时，过滤掉会员购买的路由
        if (store.getters['user/inVip'] && value.menuInfo.name === '会员购买') {
          return undefined
        }
        // TODO: 这里为特殊处理，如果有了项目登记，则抛弃委托采购
        if (
          value?.routeInfo?.meta?.grayscale === 'purchase-entrust' &&
          greyPermissionList.findIndex(grey => grey.code === 'register') > -1
        ) {
          return undefined
        }

        return value
      })
      .filter(value => value)

    store.commit('company/SET_GREY_FEATURE', greyPermissionList)

    // 获取并设置登录员工的权限
    const userRoles = userInfo.isAdmin
      ? [
          ...userInfo.employeeInfo.role,
          platformFunctionModuleDict.BUSINESS_MANAGEMENT.right,
          platformFunctionModuleDict.DEFAULT.right
        ]
      : [
          ...userInfo.employeeInfo.role,
          platformFunctionModuleDict.DEFAULT.right
        ]
    store.commit('user/SET_USER_ROLES', userRoles)

    // 处理过滤后的一维路由数组，返回前端vue-router格式的路由树
    store
      .dispatch('router/getRouterList', {
        menus: routeListTemp,
        companyInfo: companyInfo,
        platformAlias: companyInfo.platformAlias,
        roles: userRoles
      })
      .then(dynamicRoutes => {
        resolve(dynamicRoutes)
      })
      .catch(e => {
        resolve([])
      })
  })
}

/**
 * 获取公司、员工等信息，及用户vip信息
 * @returns {Promise<unknown>}
 */
function getSystemBaseInfoSync() {
  return new Promise((resolve, reject) => {
    Promise.all([
      store.dispatch('company/getUserCompanyInfo'),
      store.dispatch('user/getEmployeeInfo')
    ])
      .then(requestResult => {
        // 阿里云分析
        alifeLogger()

        // 获取灰度：没有一起请求的原因是，需要先获取到公司信息
        store
          .dispatch('company/getGrayRelease')
          .then(grayRelease => {})
          .catch(() => {})
        // 获取用户的vip信息
        store.dispatch('user/getVipInfo').catch(() => {})

        const companyInfo = requestResult[0] || {}
        const userInfo = requestResult[1] || {}

        // 设置用户角色
        const roleList = userInfo?.employeeInfo?.role?.split(',') || []
        store
          .dispatch('user/isEmployeeHasESignRight')
          .then(data => {
            if (data) {
              roleList.push(platformFunctionModuleDict.SIGNATURE_TASK.right)
            }
            userInfo.employeeInfo.role = roleList
            resolve({ companyInfo, userInfo })
          })
          .catch(() => {
            userInfo.employeeInfo.role = roleList
            resolve({ companyInfo, userInfo })
          })
      })
      .catch(err => {
        reject(err)
      })
  })
}

// 计算当前路由是否有权限
router.beforeEach(async(to, from, next) => {
  // 对所有的目标进行ac参数检查
  if ('ac' in to.query) {
    const domain = `.${window.location.hostname
      .split('.')
      .slice(-2)
      .join('.')}`

    if (to.query.ac) {
      Cookies.set('X-JCZH-AC', to.query.ac, {
        domain: domain
      })
    }

    delete to.query.ac
    next(to)
    return
  }

  try {
    if (!store?.state?.user?.userId) {
      const { purchase } = await store.dispatch('user/checkLoginStatus')

      if (to.name === 'SelectCompany') {
        next()
        return
      }

      try {
        if (!purchase) {
          throw Error('用户未登录员工身份')
        }

        const { companyInfo, userInfo } = await getSystemBaseInfoSync()
        const dynamicRoutes = await getUserRightRouteListSync(
          companyInfo,
          userInfo
        )

        const routes = [
          ...globalRoutes,
          ...dynamicRoutes,
          { path: '*', redirect: { name: 'NotFound' } }
        ]
        router.options.routes = routes
        router.addRoutes(routes)
        next({ ...to, replace: true })
        return
      } catch (e) {
        console.log('router before error：', e)
        next({
          name: 'SelectCompany',
          query: {
            jumpPath: JSON.stringify({
              name: to.name,
              path: to.path,
              query: to.query,
              params: to.params
            })
          }
        })
        return
      }
    }

    // 通过当前跳转的页面路由，设置当前所处平台
    await store.dispatch('router/setCurrentWorkbench', to)

    if (to.meta?.needFormKey && !('formKey' in to.query)) {
      const formKey = await getFormKey(to.path)
      const query = Object.assign({}, to.query, { formKey })

      next({
        name: to.name,
        query: query,
        params: to.params || {},
        replace: from.name === to.name
      })
      return
    }

    if (hasNoRight(to)) {
      next({ name: 'NotRight' })
      return
    }

    next()
  } catch (e) {
    console.error('权限配置错误：', e)
    window.onbeforeunload = null
    store
      .dispatch('user/logout')
      .then(res => {})
      .catch(err => {})
      .finally(() => {
        window.location.href = `${
          store.getters['company/loginAddress']
        }?returnUrl=${encodeURIComponent(window.location.href)}`
      })
  }
})

router.afterEach((to, from) => {
  store.commit('router/ADD_CACHED_VIEW', to)
})

router.goBack = () => {
  store.commit('router/REMOVE_CACHE_VIEW', router.currentRoute.name)
  router.back()
}
